// Role-plays and pronunciation drills — a port of `tutor/Scenarios.kt`.
// All of it ships inside the bundle, exactly as it shipped inside the APK.

export type Level = 'Beginner' | 'Intermediate' | 'Advanced';

/**
 * A guided role-play. The tutor plays `role` and walks through `script` one
 * prompt per learner turn, then falls back to free conversation when the script
 * runs out.
 */
export interface Scenario {
  id: string;
  emoji: string;
  title: string;
  subtitle: string;
  role: string;
  level: Level;
  opener: string;
  script: readonly string[];
  usefulPhrases: readonly string[];
}

export const freeTalk: Scenario = {
  id: 'free_talk',
  emoji: '💬',
  title: 'Free talk',
  subtitle: "Chat about anything — I'll correct you as we go",
  role: 'friendly tutor',
  level: 'Beginner',
  opener: "Hi! I'm your AI English tutor. 😊 Let's just chat — tell me, how was your day?",
  script: [],
  usefulPhrases: [
    'To be honest, …',
    'It depends on …',
    "I'd say that …",
  ],
};

export const scenarios: readonly Scenario[] = [
  freeTalk,
  {
    id: 'coffee_shop',
    emoji: '☕',
    title: 'Ordering coffee',
    subtitle: 'Order a drink and handle small talk at the counter',
    role: 'barista',
    level: 'Beginner',
    opener: 'Good morning! Welcome to Bluebird Coffee. What can I get for you today?',
    script: [
      'Great choice. Would you like that small, medium, or large?',
      'Sure thing. Is that for here or to take away?',
      'Can I get a name for the order?',
      "That'll be four dollars fifty. Card or cash?",
      "Perfect, it'll be ready in just a minute. Anything else with that?",
      'All done! Have a lovely day. Come back soon!',
    ],
    usefulPhrases: [
      'Could I get a large latte, please?',
      'To take away, please.',
      "I'll pay by card.",
    ],
  },
  {
    id: 'job_interview',
    emoji: '💼',
    title: 'Job interview',
    subtitle: 'Answer classic interview questions with confidence',
    role: 'hiring manager',
    level: 'Advanced',
    opener: 'Thanks for coming in today. To start, could you tell me a little about yourself?',
    script: [
      'Interesting background. Why are you interested in this role in particular?',
      'What would you say is your greatest strength?',
      "And what's a weakness you're actively working on?",
      'Tell me about a difficult problem you solved at work.',
      'Where do you see yourself in five years?',
      'Great. Do you have any questions for me?',
      "Thank you — we'll be in touch by the end of the week!",
    ],
    usefulPhrases: [
      'I have five years of experience in …',
      'One of my strengths is …',
      'In my previous role, I was responsible for …',
    ],
  },
  {
    id: 'airport',
    emoji: '✈️',
    title: 'Airport check-in',
    subtitle: 'Check a bag, pick a seat, find your gate',
    role: 'check-in agent',
    level: 'Intermediate',
    opener: 'Good afternoon! May I see your passport and booking reference, please?',
    script: [
      'Thank you. Are you checking any bags today?',
      'Would you prefer a window or an aisle seat?',
      'Did you pack your bags yourself?',
      "Here's your boarding pass. Your gate is B12, boarding starts at 3:40.",
      'Security is straight ahead on the left. Have a good flight!',
    ],
    usefulPhrases: [
      "I'd like to check one bag, please.",
      'Could I have an aisle seat?',
      'What time does boarding start?',
    ],
  },
  {
    id: 'doctor',
    emoji: '🩺',
    title: 'At the doctor',
    subtitle: 'Describe symptoms and understand advice',
    role: 'doctor',
    level: 'Intermediate',
    opener: 'Hello, come in and take a seat. What seems to be the problem today?',
    script: [
      'I see. How long have you been feeling like this?',
      'Any fever, or trouble sleeping?',
      'Are you taking any medication at the moment?',
      "Alright. I'd like you to rest and drink plenty of water.",
      "If it doesn't improve in three days, come back and see me. Take care!",
    ],
    usefulPhrases: [
      "I've had a headache since Monday.",
      'It hurts when I …',
      'Do I need a prescription?',
    ],
  },
  {
    id: 'small_talk',
    emoji: '🙂',
    title: 'Small talk',
    subtitle: 'Break the ice with a new colleague',
    role: 'new colleague',
    level: 'Beginner',
    opener: "Hi! I don't think we've met — I just joined the team this week. How long have you been here?",
    script: [
      'Nice! And what do you do here exactly?',
      "That sounds interesting. How's the workload these days?",
      'Got any plans for the weekend?',
      'Sounds good. Do you know anywhere nearby for a decent lunch?',
      "Thanks, I'll try it. Good chatting with you — see you around!",
    ],
    usefulPhrases: [
      "I've been here for about two years.",
      'How about you?',
      'Nice to meet you!',
    ],
  },
  {
    id: 'restaurant',
    emoji: '🍽️',
    title: 'At a restaurant',
    subtitle: 'Book a table, order a meal, ask for the bill',
    role: 'waiter',
    level: 'Beginner',
    opener: 'Good evening, welcome! Do you have a reservation with us tonight?',
    script: [
      'No problem, I have a table by the window. Can I start you with something to drink?',
      'Here are the menus. Are you ready to order, or shall I give you a minute?',
      'Excellent choice. Would you like any sides with that?',
      'How is everything tasting?',
      'Can I tempt you with dessert, or shall I bring the bill?',
      'Here you go. Thank you for coming — have a great evening!',
    ],
    usefulPhrases: [
      'A table for two, please.',
      'Could I see the menu?',
      'Could we get the bill, please?',
    ],
  },
  {
    id: 'hotel',
    emoji: '🏨',
    title: 'Hotel check-in',
    subtitle: 'Check in, ask about facilities, solve a problem',
    role: 'receptionist',
    level: 'Intermediate',
    opener: 'Welcome to the Grand Park Hotel! Do you have a booking with us?',
    script: [
      'Thank you. Could I see some ID, please?',
      "You're in room 507 on the fifth floor. Breakfast is served from 7 to 10.",
      'Is there anything else you need — extra towels, a wake-up call?',
      'Of course. The lift is just around the corner. Enjoy your stay!',
    ],
    usefulPhrases: [
      'I have a reservation under the name …',
      'What time is check-out?',
      'Is breakfast included?',
    ],
  },
  {
    id: 'directions',
    emoji: '🗺️',
    title: 'Asking directions',
    subtitle: 'Get around a new city without a map',
    role: 'friendly local',
    level: 'Beginner',
    opener: 'You look a bit lost — can I help you find something?',
    script: [
      "Ah, that's not far. Are you walking or taking the bus?",
      'Go straight for two blocks, then turn left at the traffic lights. Following me so far?',
      "It'll be on your right, opposite the pharmacy. Roughly ten minutes on foot.",
      'No trouble at all. Enjoy the rest of your day!',
    ],
    usefulPhrases: [
      'Excuse me, how do I get to …?',
      'Is it within walking distance?',
      'Could you say that again, please?',
    ],
  },
];

export function scenarioById(id: string): Scenario {
  return scenarios.find((s) => s.id === id) ?? freeTalk;
}

/** One sentence to read aloud for pronunciation scoring. */
export interface PracticePhrase {
  text: string;
  focus: string;
  tip: string;
}

/** Drills for the pronunciation screen, grouped by the sound they target. */
export const practicePhrases: readonly PracticePhrase[] = [
  {
    text: 'I would like a large coffee, please.',
    focus: 'Linking words',
    tip: 'Let the words run together: "I\'d-like-a-large" — don\'t pause between them.',
  },
  {
    text: 'She sells seashells by the seashore.',
    focus: '/s/ vs /ʃ/',
    tip: '"S" is a thin hiss; "sh" is rounder, with your lips pushed forward.',
  },
  {
    text: 'Think about the three thin things.',
    focus: 'The "th" sound',
    tip: 'Put your tongue lightly between your teeth. It should feel odd at first!',
  },
  {
    text: 'The weather is really lovely today.',
    focus: '/r/ and /l/',
    tip: 'For "r" the tongue never touches the roof of your mouth; for "l" it does.',
  },
  {
    text: 'I have worked here for three years.',
    focus: 'Past participle endings',
    tip: '"Worked" ends with a /t/ sound, not "work-ed".',
  },
  {
    text: 'Could you tell me where the station is?',
    focus: 'Polite question intonation',
    tip: 'Let your voice rise slightly at the end — it sounds friendlier.',
  },
  {
    text: 'It is a very interesting comfortable vegetable.',
    focus: 'Silent syllables',
    tip: 'Say "int-res-ting", "comf-ta-ble", "vej-ta-ble" — three syllables each.',
  },
  {
    text: 'My birthday is on the thirtieth of March.',
    focus: 'Ordinal numbers',
    tip: '"Thirtieth" has the same "th" as "think" at the end.',
  },
  {
    text: 'I am looking forward to meeting you.',
    focus: 'Weak forms',
    tip: '"To" becomes a quick "tuh" — native speakers never stress it.',
  },
  {
    text: 'Where did you park the car?',
    focus: 'Connected speech',
    tip: '"Did you" naturally becomes "did-ja" in fast speech.',
  },
  {
    text: 'This restaurant serves excellent vegetarian food.',
    focus: 'Word stress',
    tip: 'Stress the second syllable: res-TAU-rant, ve-ge-TA-rian.',
  },
  {
    text: 'I have been studying English for two years.',
    focus: 'Present perfect rhythm',
    tip: '"Have been" is said quickly and softly — the stress lands on "studying".',
  },
];
